import {useCallback, useState} from 'react'
import {Banner, Button, Checkbox, FormControl, Link} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {SplashDialogImage} from '@github-ui/splash-dialog'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {
  requestCopilotCodingAgentAccess,
  enableCopilotCodingAgentPolicy,
  removeCopilotCodingAgentRequest,
} from '@github-ui/copilot-member-feature-requests'
import {COPILOT_CODING_AGENT_DOCS_URL, buildEnterpriseCopilotCodingAgentPolicyUrl} from '../../utils/constants'
import type {CopilotCodingAgentEligibility} from './copilot-assignment-types'
import styles from './CopilotCodingAgentRequestAccessDialog.module.css'

export const HIDE_CCA_REQUEST_DIALOG_KEY = 'hideCopilotCodingAgentRequestDialog'

export interface CopilotCodingAgentRequestAccessDialogProps {
  onClose: () => void
  eligibility: CopilotCodingAgentEligibility
  orgLogin: string
  businessSlug?: string
  onEligibilityUpdate: () => Promise<void>
  onRequestSuccess: (actionType?: 'request' | 'enable', orgLogin?: string) => void
}

export function CopilotCodingAgentRequestAccessDialog({
  onClose,
  eligibility,
  orgLogin,
  businessSlug,
  onEligibilityUpdate,
  onRequestSuccess,
}: CopilotCodingAgentRequestAccessDialogProps) {
  const [doNotShowAgain, setDoNotShowAgain] = useLocalStorage(HIDE_CCA_REQUEST_DIALOG_KEY, false)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.currentTarget.checked
    setDoNotShowAgain(checked)

    if (checked) {
      sendClickAnalyticsEvent({
        category: 'member_feature_request',
        action: 'action.copilot_coding_agent.do_not_show',
        label: 'ref_cta:do_not_show;ref_loc:issue_assignment_dialog',
      })
    }
  }

  const handleRequestAccess = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    sendClickAnalyticsEvent({
      category: 'member_feature_request',
      action: 'action.copilot_coding_agent',
      label: 'ref_cta:request_access;ref_loc:issue_assignment_dialog;',
    })

    try {
      await requestCopilotCodingAgentAccess(orgLogin)
      await onEligibilityUpdate()
      onRequestSuccess('request', orgLogin)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, sendClickAnalyticsEvent, onEligibilityUpdate, onRequestSuccess, onClose])

  const handleEnableFeature = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    sendClickAnalyticsEvent({
      category: 'member_feature_request',
      action: 'action.copilot_coding_agent.enable',
      label: 'ref_cta:enable_feature;ref_loc:issue_assignment_dialog;',
    })

    try {
      await enableCopilotCodingAgentPolicy(orgLogin)
      await onEligibilityUpdate()
      onRequestSuccess('enable', orgLogin)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, sendClickAnalyticsEvent, onEligibilityUpdate, onRequestSuccess, onClose])

  const handleRemoveRequest = useCallback(async () => {
    if (!orgLogin || isSubmitting) return

    setIsSubmitting(true)
    setError(null)

    try {
      await removeCopilotCodingAgentRequest(orgLogin)
      await onEligibilityUpdate()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }, [orgLogin, isSubmitting, onEligibilityUpdate, onClose])

  const handleNavigateToEnterpriseSettings = useCallback(() => {
    sendClickAnalyticsEvent({
      category: 'member_feature_request',
      action: 'action.copilot_coding_agent.set_enterprise_policies',
      label: 'ref_cta:set_enterprise_policies;ref_loc:issue_assignment_dialog;',
    })

    const enterpriseSlug = businessSlug || orgLogin
    window.location.href = buildEnterpriseCopilotCodingAgentPolicyUrl(enterpriseSlug)
  }, [businessSlug, orgLogin, sendClickAnalyticsEvent])

  const handleButtonClick = useCallback(async () => {
    if (eligibility.canGrantEnterpriseCca) {
      handleNavigateToEnterpriseSettings()
    } else if (eligibility.canGrantOrgCca) {
      await handleEnableFeature()
    } else if (eligibility.hasRequestedCca) {
      await handleRemoveRequest()
    } else if (eligibility.canRequestCca) {
      await handleRequestAccess()
    }
  }, [
    eligibility.canGrantEnterpriseCca,
    eligibility.canGrantOrgCca,
    eligibility.hasRequestedCca,
    eligibility.canRequestCca,
    handleNavigateToEnterpriseSettings,
    handleEnableFeature,
    handleRemoveRequest,
    handleRequestAccess,
  ])

  let buttonText = 'Request access'
  if (eligibility.canGrantEnterpriseCca) {
    buttonText = 'See Copilot policies'
  } else if (eligibility.canGrantOrgCca) {
    buttonText = isSubmitting ? 'Enabling...' : 'Enable feature'
  } else if (eligibility.hasRequestedCca) {
    buttonText = isSubmitting ? 'Removing...' : 'Remove request'
  } else if (eligibility.canRequestCca) {
    buttonText = isSubmitting ? 'Requesting...' : 'Request access'
  }

  let descriptionText =
    'Request access from your admin, and let GitHub Copilot work independently in the background to complete tasks, with Copilot coding agent.'

  if (eligibility.canGrantEnterpriseCca || eligibility.canGrantOrgCca) {
    descriptionText =
      'Enable Copilot coding agent to let GitHub Copilot work independently in the background to complete tasks.'
  } else if (eligibility.hasRequestedCca) {
    descriptionText = 'You have requested access to Copilot coding agent. Your admin will review your request.'
  }

  return (
    <Dialog
      onClose={onClose}
      width="large"
      renderHeader={({dialogLabelId}) => (
        <>
          <div className={styles.headerContainer}>
            <SplashDialogImage
              darkImagePath="/static/images/modules/swe_agent/agent-task-dark.png"
              lightImagePath="/static/images/modules/swe_agent/agent-task-light.png"
              // eslint-disable-next-line @github-ui/github-monorepo/no-sx -- SplashDialogImage from @github-ui/splash-dialog only accepts sx prop
              sx={{
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px',
              }}
            />
            <div className={styles.closeButtonContainer}>
              <Dialog.CloseButton onClose={onClose} />
            </div>
          </div>
          <Dialog.Header id={dialogLabelId} className={styles.visuallyHidden}>
            <Dialog.Title>Copilot coding agent is disabled</Dialog.Title>
          </Dialog.Header>
        </>
      )}
    >
      <div className={styles.body}>
        <h2 className={styles.heading}>Copilot coding agent is disabled</h2>
        <p className={styles.description}>
          {descriptionText}{' '}
          <Link href={COPILOT_CODING_AGENT_DOCS_URL} target="_blank" rel="noreferrer">
            Learn more
          </Link>
          .
        </p>
        {error && (
          <div aria-live="assertive" className={styles.errorContainer}>
            <Banner variant="critical" title="Error" hideTitle description={error} />
          </div>
        )}
        <div className={styles.footer}>
          <FormControl>
            <Checkbox checked={doNotShowAgain} onChange={handleCheckboxChange} />
            <FormControl.Label>Don&apos;t show this again</FormControl.Label>
          </FormControl>
          <Button onClick={handleButtonClick} disabled={isSubmitting}>
            {buttonText}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

try{ CopilotCodingAgentRequestAccessDialog.displayName ||= 'CopilotCodingAgentRequestAccessDialog' } catch {}